import { useT } from '../context/LanguageContext';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Clapperboard, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { AddSocialPostDialog } from './AddSocialPostDialog';
import { SocialPostCard } from './SocialPostCard';

export const useSocialPosts = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState('loading');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (user === null) return;
    let active = true;
    setStatus('loading');
    api.get('/social-posts', { timeout: 12000 })
      .then((r) => {
        if (!active) return;
        setPosts(Array.isArray(r.data) ? r.data : []);
        setStatus('loaded');
      })
      .catch(() => { if (active) setStatus('error'); });
    return () => { active = false; };
  }, [user, refresh]);
  const reload = () => setRefresh((value) => value + 1);
  const update = (p) => setPosts((list) => list.map((x) => (x.id === p.id ? p : x)));
  const remove = (id) => setPosts((list) => list.filter((x) => x.id !== id));
  const add = (p) => { setPosts((list) => [p, ...list]); setStatus('loaded'); };
  return { posts, status, reload, update, remove, add };
};

const SocialFeed = () => {
  const { t } = useT();
  const { user } = useAuth();
  const { posts, status, reload, update, remove, add } = useSocialPosts();
  const [open, setOpen] = useState(false);
  const isAdmin = !!user?.is_admin;

  if (posts.length === 0 && !isAdmin && status !== 'error') return null;
  const visible = posts.slice(0, 3);

  return (
    <section id="videoar" className="bg-black py-20 md:py-24 border-t border-white/5" data-testid="social-feed">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-sm mb-2">{t('Frå TikTok, Instagram & Refuel')}</p>
            <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tighter" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t('Sjå stemninga')}</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {posts.length > 3 && (
              <Link to="/videoar" data-testid="social-all-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-white/20 text-white font-bold hover:border-[#ff2e2e] hover:text-[#ff2e2e] transition-colors">
                {t('Sjå alle')} ({posts.length}) <ArrowRight size={18} />
              </Link>
            )}
            {isAdmin && (
              <button onClick={() => setOpen(true)} data-testid="social-add-button" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#ff2e2e] text-black font-bold hover:bg-[#ff4d4d] transition-colors shadow-lg shadow-[#ff2e2e]/30">
                <Plus size={18} /> {t('Legg til video')}
              </button>
            )}
          </div>
        </div>

        {status === 'error' ? (
          <div role="status" className="rounded-3xl border border-amber-400/20 bg-amber-400/5 p-8 text-center text-white/70">
            <p>Videoane kunne ikkje lastast akkurat no.</p>
            <button type="button" onClick={reload} className="mt-3 font-semibold text-[#ffb400] hover:text-white">Prøv igjen</button>
          </div>
        ) : posts.length === 0 ? (
          <div data-testid="social-empty" className="rounded-3xl border border-dashed border-white/15 p-10 text-center text-white/50 flex flex-col items-center gap-3">
            <Clapperboard size={32} className="text-[#ff2e2e]" />
            Ingen videoar enno. Trykk «Legg til video» for å publisere den første.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-sm sm:max-w-none mx-auto items-start" data-testid="social-grid">
            {visible.map((p) => <SocialPostCard key={p.id} post={p} onChange={update} onDelete={remove} />)}
          </div>
        )}
        {posts.length > 3 && (
          <div className="flex justify-center mt-10 sm:hidden">
            <Link to="/videoar" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-white/20 text-white font-bold">{t('Sjå alle videoar')} <ArrowRight size={18} /></Link>
          </div>
        )}
      </div>
      <AddSocialPostDialog open={open} onClose={() => setOpen(false)} onCreated={add} />
    </section>
  );
};

export default SocialFeed;
